import { Messages } from '../../codegen/Localize';
import { WebClientDetailFetcher } from '../../commonInfra/utils/resourceFetchers/WebClientDetailFetcher';
import { WebClientListResourceFetcher } from '../../commonInfra/utils/resourceFetchers/WebClientListResourceFetcher';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { RequestHelper } from '../../httpClient/utils/RequestHelper';
import { ResultHelper } from '../../httpClient/utils/ResultHelper';
import WebHttpClient from '../../httpClient/WebHttpClient';
import { WebHttpClientWithErrorToast, WebHttpClientWithErrorToastInstance } from '../../httpClient/WebHttpClientWithErrorToast';
import { IDetailFetcher } from '../../ts/fetchers/IDetailFetcher';
import { IListFetcher } from '../../ts/fetchers/IListFetcher';
import { ServerApiMapper } from '../../ts/helpers/ServerModelHelper';
import { MetricMapper } from '../../ts/mappers/MetricMapper';
import { FieldValuesQuery } from '../../ts/resources/FieldValuesQuery';
import { IActionResource, IDetailResource, IFieldValuesResource, IListResource } from '../../ts/resources/Resource';
import { ResourceQuery } from '../../ts/resources/ResourceQuery';
import { AddTagsToMetricsRequest } from '../../ts/ServerApi/requests/AddTagsToMetricsRequest';
import { DeleteMetricsRequest } from '../../ts/ServerApi/requests/DeleteMetricsRequest';
import { GetFieldValuesRequest } from '../../ts/ServerApi/requests/GetFieldValuesRequest';
import { GetMetricActionsRequest } from '../../ts/ServerApi/requests/GetMetricActionsRequest';
import { GetMetricsRequest } from '../../ts/ServerApi/requests/GetMetricsRequest';
import { MarkRecentlyViewedRequest, RecentlyViewedObjectType } from '../../ts/ServerApi/requests/MarkRecentlyViewedRequest';
import { MarkViewedRequest, ViewedObjectType } from '../../ts/ServerApi/requests/MarkViewedRequest';
import { MoveMetricsToProject } from '../../ts/ServerApi/requests/MoveMetricsToProjectRequest';
import { RemoveTagsFromMetricsRequest } from '../../ts/ServerApi/requests/RemoveTagsFromMetricsRequest';
import { RenameMetricRequest } from '../../ts/ServerApi/requests/RenameMetricRequest';
import { IMetricDescriptionParams, SetMetricDescriptionRequest } from '../../ts/ServerApi/requests/SetMetricDescriptionRequest';
import { SetMetricsOwnerRequest } from '../../ts/ServerApi/requests/SetMetricsOwnerRequest';
import { SetMetricsSuspendedStateRequest } from '../../ts/ServerApi/requests/SetMetricsSuspendedStateRequest';
import { IFieldValue } from '../../ts/ServerApi/types/IFieldValue';
import { IMetricActions } from '../../ts/ServerApi/types/IMetricActions';
import { ToasterService, ToastType } from '../../ts/services/ToasterService';
import { IMetricInfo } from '../models/IMetricInfo';

class MetricsResource implements IListResource<IMetricInfo>, IFieldValuesResource, IActionResource<IMetricActions>, IDetailResource<IMetricInfo> {
  private listFetcher: IListFetcher<IMetricInfo>;
  private fieldValuesFetcher: IListFetcher<IFieldValue>;
  private detailFetcher: IDetailFetcher<IMetricInfo>;

  constructor() {
    this.listFetcher = new WebClientListResourceFetcher(GetMetricsRequest, ServerApiMapper.metrics);
    this.fieldValuesFetcher = new WebClientListResourceFetcher(GetFieldValuesRequest, ServerApiMapper.fieldValues);
    this.detailFetcher = new WebClientDetailFetcher('getMetric', MetricMapper.mapDetail);
  }

  public all(): ResourceQuery<IMetricInfo> {
    return new ResourceQuery(this.listFetcher);
  }

  public valuesForField(field: string): FieldValuesQuery {
    return new FieldValuesQuery(this.fieldValuesFetcher, field, 'metric');
  }

  public getById(id: string): Promise<IMetricInfo> {
    return this.detailFetcher.fetchById(id);
  }

  private getErrorNotifyingServer(): WebHttpClientWithErrorToast {
    return WebHttpClientWithErrorToastInstance;
  }

  // Actions

  public actions(id: string): Promise<IMetricActions> {
    return WebHttpClient.sendRequest(new GetMetricActionsRequest(id));
  }

  public del(ids: string[]): Promise<IResult> {
    return this.getErrorNotifyingServer().sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) => new DeleteMetricsRequest(idsBlock)),
      ResultHelper.combineResults);
  }

  public setOwner(ids: string[], ownerId: string): Promise<IResult> {
    return this.getErrorNotifyingServer().sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) => new SetMetricsOwnerRequest(idsBlock, ownerId)),
      ResultHelper.combineResults);
  }

  public setMetricDescription = async (args: IMetricDescriptionParams): Promise<IResult> => {
    return this.getErrorNotifyingServer()
      .sendRequestAndDelay(new SetMetricDescriptionRequest(args))
      .then(r => this.toast(r, Messages.editDescriptionSuccessful(), Messages.editDescriptionFailure()));
  }

  public rename(id: string, name: string): Promise<IResult> {
    return this.getErrorNotifyingServer().sendRequestAndDelay(new RenameMetricRequest(id, name));
  }

  public moveToProject(targetProjectId: string, metricIds: string[]): Promise<IResult> {
    return this.getErrorNotifyingServer().sendRequestsAndDelay(
      RequestHelper.buildRequests(metricIds,
        (idsBlock) => new MoveMetricsToProject({
          ids: idsBlock,
          projectId: targetProjectId
        })),
      ResultHelper.combineResults);
  }

  public addTags(ids: string[], tags: string[]): Promise<IResult> {
    return (tags.length > 0)
      ? this.getErrorNotifyingServer().sendRequestsAndDelay(
          RequestHelper.buildRequests2(ids, tags,
            (idsBlock, tagsBlock) => new AddTagsToMetricsRequest(idsBlock, tagsBlock)),
          ResultHelper.combineResults)
      : Promise.resolve({});
  }

  public removeTags(ids: string[], tags: string[]): Promise<IResult> {
    return (tags.length > 0)
      ? this.getErrorNotifyingServer().sendRequestsAndDelay(
          RequestHelper.buildRequests2(ids, tags,
            (idsBlock, tagsBlock) => new RemoveTagsFromMetricsRequest(idsBlock, tagsBlock)),
          ResultHelper.combineResults)
      : Promise.resolve({});
  }

  public changeTags(ids: string[], tagsToAdd: string[], tagsToRemove: string[]): Promise<IResult> {
    return this.removeTags(ids, tagsToRemove)
      .then((result: IResult) => (ResultHelper.hasErrors(result) ? result : this.addTags(ids, tagsToAdd)));
  }

  public isMetricInfo(item: any): item is IMetricInfo {
    return item &&
           item.contentType &&
           item.contentType === 'metric' &&
           item.hasOwnProperty('connectedView');
  }

  public markRecentlyViewed(id: string): Promise<IResult> {
    return WebHttpClient.sendRequest(new MarkRecentlyViewedRequest({
      objectId: id,
      objectType: RecentlyViewedObjectType.Metric
    }));
  }

  public markViewed(id: string): Promise<IResult> {
    return WebHttpClient.sendRequest(new MarkViewedRequest({
      objectId: id,
      objectType: ViewedObjectType.Metric
    }));
  }

  public resumeMetrics(ids: string[]): Promise<IResult> {
    return WebHttpClient.sendRequest(new SetMetricsSuspendedStateRequest(false, ids));
  }

  public toast(result: IResult, successMessage: string, failureMessage: string): any {
    if (result.errors && result.errors.length > 0) {
      ToasterService.instance.pop({
        type: ToastType.Error,
        text: failureMessage
      });
    } else {
      ToasterService.instance.pop({
        type: ToastType.Success,
        text: successMessage
      });
    }
  }
}

export const Metrics = new MetricsResource();
